import { useCallback } from 'react';

const EMPTY_ARRAY = [];
const EMPTY_LABELS = {};
import { AlertCircle, SearchX } from 'lucide-react';
import IconHighlight from '../../IconHighlight.jsx';
import Skeleton from '../../Skeleton.jsx';
import TopbarGlobalSearchFilterSummary from './TopbarGlobalSearchFilterSummary.jsx';
import TopbarGlobalSearchResultRow from './TopbarGlobalSearchResultRow.jsx';
import {
  GLOBAL_SEARCH_ACCENT_TEXT_CLASS,
  GLOBAL_SEARCH_KEYCAP_CLASS,
  GLOBAL_SEARCH_SECTION_LABEL_CLASS,
  GLOBAL_SEARCH_SURFACE_CLASS,
} from './constants.js';

/**
 * @typedef {'idle'|'loading'|'results'|'empty'|'error'} GlobalSearchBodyStatus
 */

/**
 * @param {object} props
 * @param {GlobalSearchBodyStatus} props.status
 * @param {Array<{ id: string, label: string, icon?: import('lucide-react').LucideIcon, shortcutKey?: string, onSelect?: () => void }>} [props.suggestedActions]
 * @param {Array<object>} [props.recentItems]
 * @param {Array<object>} [props.results] - Flat results list (used when groupedResults is empty)
 * @param {Array<{ id: string, label: string, icon?: import('lucide-react').LucideIcon, items: Array<object>, totalCount?: number, onSeeAll?: () => void }>} [props.groupedResults]
 * @param {number} [props.highlightedIndex] - Flat index across all visible result rows
 * @param {string} [props.query] - Active search query for text highlight
 * @param {(item: object) => void} [props.onSelectResult]
 * @param {(action: object) => void} [props.onSelectAction]
 * @param {() => void} [props.onClearFilters]
 * @param {() => void} [props.onRetry]
 * @param {Array<{ id: string, label: string }>} [props.activeFilters]
 * @param {() => void} [props.onSearchAll]
 * @param {Record<string, string>} [props.labels]
 */
const TopbarGlobalSearchBody = ({
  status = 'idle',
  suggestedActions = EMPTY_ARRAY,
  recentItems = EMPTY_ARRAY,
  results = EMPTY_ARRAY,
  groupedResults = EMPTY_ARRAY,
  highlightedIndex = -1,
  query = '',
  onSelectResult,
  onSelectAction,
  onClearFilters,
  onRetry,
  onSearchAll,
  activeFilters = EMPTY_ARRAY,
  labels = EMPTY_LABELS,
}) => {
  const handleSelectResult = useCallback(
    (item) => onSelectResult?.(item),
    [onSelectResult],
  );

  const handleSelectAction = useCallback(
    (action) => onSelectAction?.(action),
    [onSelectAction],
  );

  const filterSummary = (
    <TopbarGlobalSearchFilterSummary
      activeFilters={activeFilters}
      labels={labels}
      onClearFilters={onClearFilters}
      onSearchAll={onSearchAll}
    />
  );

  if (status === 'loading') {
    return (
      <div className={['flex min-h-0 flex-1 flex-col', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
        {filterSummary}
        <div className="scrollbar-aimsos-auto-sm min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
          {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="px-2 py-1.5">
              <Skeleton height={44} borderRadius={12} className="w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={['flex min-h-0 flex-1 flex-col', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
        {filterSummary}
        <div className="flex min-h-0 flex-1 items-start px-4 py-4">
          <div className="flex w-full items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-500/20 dark:bg-red-950/20">
            <AlertCircle className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400" strokeWidth={2} aria-hidden />
            <p className="flex-1 text-sm font-medium text-red-700 dark:text-red-300">
              {labels.errorMessage ?? 'Something went wrong. Please try again.'}
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className="shrink-0 text-sm font-semibold text-red-700 transition-colors hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"
              >
                {labels.retry ?? 'Retry'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'empty') {
    return (
      <div className={['flex min-h-0 flex-1 flex-col', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
        {filterSummary}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-4 py-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <IconHighlight
              icon={SearchX}
              bgColor="Primary"
              iconColor="PrimaryAlt"
              size="medium"
              className="!h-10 !w-10 !min-h-10 !min-w-10 bg-violet-50 dark:bg-violet-950/30"
            />
            <p className="text-base font-semibold text-content dark:text-gray-100">
              {labels.noResultsTitle ?? 'No matches found'}
            </p>
            <p className="text-sm font-medium leading-5 text-[#5C5C5C] dark:text-gray-400">
              {labels.noResultsDescription ?? 'Try fewer words or different keywords'}
            </p>
          </div>
          {onClearFilters ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium leading-5 text-[#2A2A2A] transition-colors hover:bg-black/[0.04] dark:text-gray-200 dark:hover:bg-white/[0.06]"
            >
              {labels.clearFilters ?? 'Clear filters'}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (status === 'results') {
    const hasGrouped = groupedResults.length > 0;
    let flatIndex = 0;

    return (
      <div className={['flex min-h-0 flex-1 flex-col', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
        {filterSummary}
        <div className="scrollbar-aimsos-auto-sm min-h-0 flex-1 overflow-y-auto">
          {hasGrouped ? (
            groupedResults.map((group) => {
              const GroupIcon = group.icon;
              return (
                <section key={group.id} className="pb-1">
                  <p className={[GLOBAL_SEARCH_SECTION_LABEL_CLASS, 'flex items-center gap-1.5'].join(' ')}>
                    {GroupIcon ? (
                      <GroupIcon className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
                    ) : null}
                    {group.label}
                    {typeof group.totalCount === 'number' ? (
                      <span className="font-normal normal-case opacity-60">{group.totalCount}</span>
                    ) : null}
                  </p>
                  {group.items.map((item) => {
                    const itemIndex = flatIndex++;
                    return (
                      <TopbarGlobalSearchResultRow
                        key={item.id ?? itemIndex}
                        {...item}
                        query={query}
                        isHighlighted={highlightedIndex === itemIndex}
                        onSelect={item.onSelect}
                        onSelectFallback={handleSelectResult}
                        data={item}
                      />
                    );
                  })}
                  {group.onSeeAll && typeof group.totalCount === 'number' && group.totalCount > group.items.length ? (
                    <button
                      type="button"
                      onClick={group.onSeeAll}
                      className={['px-4 py-1.5 text-xs font-medium', GLOBAL_SEARCH_ACCENT_TEXT_CLASS].join(' ')}
                    >
                      {labels.seeAll
                        ? `${labels.seeAll} ${group.totalCount}`
                        : `See all ${group.totalCount}`}
                    </button>
                  ) : null}
                </section>
              );
            })
          ) : (
            results.map((item, index) => (
              <TopbarGlobalSearchResultRow
                key={item.id ?? index}
                {...item}
                query={query}
                isHighlighted={highlightedIndex === index}
                onSelect={item.onSelect}
                onSelectFallback={handleSelectResult}
                data={item}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  // idle
  return (
    <div className={['flex min-h-0 flex-1 flex-col', GLOBAL_SEARCH_SURFACE_CLASS].join(' ')}>
      {filterSummary}
      <div className="scrollbar-aimsos-auto-sm min-h-0 flex-1 overflow-y-auto">
        {suggestedActions.length > 0 ? (
          <section className="pb-1">
            <p className={GLOBAL_SEARCH_SECTION_LABEL_CLASS}>
              {labels.suggestedActions ?? 'Suggested actions'}
            </p>
            {suggestedActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  type="button"
                  onClick={() => (action.onSelect ? action.onSelect() : handleSelectAction(action))}
                  className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
                >
                  {Icon ? (
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#F2F2F2] dark:bg-[#1e2433]">
                      <Icon className="h-4 w-4 text-content dark:text-gray-300" strokeWidth={2} aria-hidden />
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 text-sm font-medium leading-5 text-[#2A2A2A] dark:text-gray-100">
                    {action.label}
                  </span>
                  {action.shortcutKey ? (
                    <span className={[GLOBAL_SEARCH_KEYCAP_CLASS, 'w-6 px-0'].join(' ')} aria-hidden>
                      {action.shortcutKey}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </section>
        ) : null}

        {recentItems.length > 0 ? (
          <section className="pb-2">
            <p className={GLOBAL_SEARCH_SECTION_LABEL_CLASS}>
              {labels.popularThisWeek ?? labels.recentSearches ?? 'Popular this week'}
            </p>
            {recentItems.map((item, index) => (
              <TopbarGlobalSearchResultRow
                key={item.id ?? index}
                {...item}
                onSelect={item.onSelect}
                onSelectFallback={handleSelectResult}
                data={item}
              />
            ))}
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default TopbarGlobalSearchBody;
